import Address from './Address';
import BaseModel from './BaseModel';
import Equipment from './Equipment';
import ProjectPart from './ProjectPart';
import Resource from './Resource';
import * as AT from '../../components/container/attributes';
import {HitActionUtils} from '@hit/components/src/utils/action/HitActionUtils';
import Staff from './Staff';
import {
  useActionStore,
  useConfigurationStore,
  useUserProfileStore,
} from '../../store/pinia';
import {HitOnlineEnums} from 'hit-online-web-ui/src/helpers/constants';
import {translate} from '../../plugins/i18n/i18n';

function dueDateValidation(dueDate) {
  if (dueDate) {
    const minDate = new Date('1970-01-01');
    const dueDateDate = new Date(dueDate);
    return dueDateDate >= minDate;
  }
  return true;
}

export default class Action extends BaseModel {
  constructor({
    id,
    number,
    designation,
    dueDate = undefined,
    author = undefined,
    assignee = undefined,
    importance = undefined,
    urgency = undefined,
    status = undefined,
    tags = [],
    url = '',
    address = undefined,
    staff = undefined,
    equipment = undefined,
    resource = undefined,
    projectPart = undefined,
    privateFlag = false,
    withoutDueDate = false,
  }) {
    super(id, number, designation);
    this.dueDate = dueDate;
    this.author = author ? new Staff(author) : null;
    this.assignee = assignee ? new Staff(assignee) : null;
    this.importance = importance;
    this.urgency = urgency;
    this.tags = tags;
    this.url = url;
    this.address = address ? new Address(address) : null;
    this.staff = staff ? new Staff(staff) : null;
    this.equipment = equipment ? new Equipment(equipment) : null;
    this.resource = resource ? new Resource(resource) : null;
    this.projectPart = projectPart ? new ProjectPart(projectPart) : null;
    this.status = status
      ? HitOnlineEnums.getEnumValue('ACTION_STATUS', status, translate)
      : null;
    this.privateFlag = privateFlag;
    this.withoutDueDate = withoutDueDate;
  }

  static get config() {
    const config = BaseModel.config;
    config['importable'] = false;
    return config;
  }

  get config() {
    return Action.config;
  }

  static get fkName() {
    return 'action_id';
  }
  static get apiRoute() {
    return 'action';
  }
  static get detailPageRoute() {
    return 'actionDetail';
  }
  static get listPageRoute() {
    return 'actionList';
  }
  get fkName() {
    return Action.fkName;
  }
  get apiRoute() {
    return Action.apiRoute;
  }
  get listPageRoute() {
    return Action.listPageRoute;
  }
  get detailPageRoute() {
    return Action.detailPageRoute;
  }

  static get defaultSort() {
    return {
      property: 'due_date',
      order: 'desc',
    };
  }

  getDefaultSort() {
    return Action.defaultSort;
  }

  static get joinAttrsKey() {
    return ['number', 'designation', 'dueDate'];
  }

  static get listDisplayAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'dueDate',
      'author',
      'assignee',
      'importance',
      'urgency',
      'status',
      'tags',
      'creationDate',
      'modificationDate',
    ]);
  }

  static get infoAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'dueDate',
      'url',
      'importance',
      'urgency',
      'tags',
      'assignee',
      'estimatedDuration',
      'author',
      'status',
      'address',
      'staff',
      'equipment',
      'resource',
      'projectPart',
      'privateFlag',
      'withoutDueDate',
    ]);
  }

  static get detailAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'description',
      'assignee',
      'author',
      'status',
      'address',
      'staff',
      'equipment',
      'resource',
      'projectPart',
    ]);
  }

  static get entityListDisplayAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'dueDate',
      'assignee',
      'importance',
      'urgency',
      'status',
      'tags',
    ]);
  }

  static get linkedEntitiesAttrs() {
    return this.getAttributes([
      'address',
      'staff',
      'equipment',
      'resource',
      'projectPart',
      'projectPartItem',
    ]);
  }

  static get browserDisplayAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'author',
      'assignee',
      'dueDate',
      'status',
      'tags',
    ]);
  }

  static get createInitColumns() {
    return {
      designation: '',
      due_date: HitActionUtils.getDefaultDueDate(),
      status: HitActionUtils.Status.ASSIGNED,
      assignee_id: useUserProfileStore().staffId,
    };
  }

  static get bulkUpdateAttrs() {
    return {
      'hit-base.bulk-update.general-information': this.getAttributes([
        'number',
        'designation',
        'dueDate',
        'importance',
        'status',
        'urgency',
        'url',
        'tags',
      ]),
      'hit-app.common.linked-entity': this.getAttributes([
        'address',
        'staff',
        'equipment',
        'resource',
        'projectPart',
      ]),
    };
  }
  static get allAttributes() {
    return [
      this.NUMBER_ATTRIBUTE(),
      this.DESIGNATION_ATTRIBUTE(),
      new AT.RichTextAttribute({
        name: 'description',
        tableProperties: {
          header: 'hit-base.common.description',
          maxWidth: '1fr',
        },
      }),
      new AT.DateAttribute({
        name: 'dueDate',
        validations: {dueDateValidation},
        column: 'due_date',
        tableProperties: {
          header: 'hit-app.common.due-date',
          maxWidth: '1fr',
        },
      }),
      new AT.FkStaffAttribute({
        name: 'author',
        column: 'creation_user_id',
        joinPath: ['staff!fk_action_creation_user_id_staff'],
        fkToUpdate: 'creation_user_id',
        tableProperties: {
          header: 'hit-base.common.author',
        },
      }),
      new AT.FkStaffAttribute({
        name: 'assignee',
        column: 'assignee_id',
        joinPath: ['staff!fk_action_assignee_id_staff'],
        fkToUpdate: 'assignee_id',
        tableProperties: {
          header: 'hit-base.common.assignee',
          maxWidth: '1fr',
        },
      }),
      new AT.DecimalAttribute({
        name: 'importance',
        tableProperties: {
          header: 'hit-app.common.importance',
          maxWidth: '1fr',
          hideByDefault: true,
        },
      }),
      new AT.DecimalAttribute({
        name: 'urgency',
        tableProperties: {
          header: 'hit-app.common.urgency',
          maxWidth: '1fr',
          hideByDefault: true,
        },
      }),
      new AT.StringAttribute({
        name: 'url',
        tableProperties: {
          header: 'hit-app.common.url',
        },
      }),
      new AT.EnumerationAttribute({
        name: 'status',
        enumType: 'ACTION_STATUS',
        enumClass: HitOnlineEnums,
        tableProperties: {
          header: 'hit-app.common.status',
          maxWidth: '0.75fr',
        },
      }),
      this.TAGS_ATTRIBUTE({moduleUsage: 'action', tableOrigin: 'action'}),
      new AT.FkAddressAttribute({
        name: 'address',
        joinPath: ['address!fk_action_address_id_address'],
        column: 'address_id',
        tableProperties: {
          header: 'hit-base.common.address',
        },
      }),
      new AT.FkStaffAttribute({
        name: 'staff',
        joinPath: ['staff!fk_action_staff_id_staff'],
        column: 'staff_id',
        tableProperties: {
          header: 'hit-base.common.staff',
        },
      }),
      new AT.FkEquipmentAttribute({
        name: 'equipment',
        joinPath: ['equipment!fk_action_equipment_id_equipment'],
        column: 'equipment_id',
        tableProperties: {
          header: 'hit-base.common.equipment',
        },
      }),
      new AT.FkResourceAttribute({
        name: 'resource',
        joinPath: ['resource!fk_action_resource_id_resource'],
        column: 'resource_id',
        tableProperties: {
          header: 'hit-base.common.resource',
        },
      }),
      new AT.FkProjectPartAttribute({
        name: 'projectPart',
        joinPath: ['project_part!fk_action_project_part_id_project_part'],
        column: 'project_part_id',
        tableProperties: {
          header: 'hit-base.common.project-part',
        },
      }),
      new AT.FkPositionItemAttribute({
        name: 'projectPartItem',
        joinPath: [
          'project_part_item!fk_action_project_part_item_id_project_part_item',
        ],
        column: 'project_part_item_id',
        tableProperties: {
          header: 'hit-base.common.project-part-item',
        },
      }),
      new AT.StringAttribute({
        name: 'estimatedDuration',
        column: 'estimated_duration',
        tableProperties: {
          header: 'hit-app.common.estimated-duration',
        },
      }),
      new AT.BooleanAttribute({
        name: 'privateFlag',
        column: 'private',
        tableProperties: {
          header: 'hit-app.action.private',
        },
      }),
      new AT.DateAttribute({
        name: 'creationDate',
        column: 'creation_date',
        tableProperties: {
          header: 'hit-base.common.creation-date',
          hideByDefault: true,
        },
      }),
      new AT.DateAttribute({
        name: 'modificationDate',
        column: 'modification_date',
        tableProperties: {
          header: 'hit-base.common.last-modified',
          hideByDefault: true,
        },
      }),
      new AT.BooleanAttribute({
        name: 'withoutDueDate',
        column: 'without_due_date',
        tableProperties: {
          header: 'hit-app.action.without-due-date',
        },
      }),
    ];
  }

  static get allHistoryAttributes() {
    return [
      new AT.DateAttribute({
        name: 'date',
        column: 'operation_date',
        tableProperties: {
          header: 'hit-app.common.date',
        },
      }),
      new AT.FkStaffAttribute({
        name: 'operator',
        joinPath: ['staff!fk_action_history_creation_user_id_staff'],
        column: 'creation_user_id',
        tableProperties: {
          header: 'hit-app.action.operator',
        },
      }),
      new AT.StringAttribute({
        name: 'duration',
        transform: (d) =>
          HitActionUtils.stringifyDuration(
            d,
            useConfigurationStore().userLanguageSnakeCase,
          ),
        tableProperties: {
          header: 'hit-app.common.duration',
        },
      }),
      new AT.EnumerationAttribute({
        name: 'operation',
        enumType: 'ACTION_OPERATION',
        enumClass: HitOnlineEnums,
        tableProperties: {
          header: 'hit-app.common.operation',
        },
      }),
      new AT.StringAttribute({
        name: 'message',
        tableProperties: {
          header: 'hit-app.action.message',
        },
      }),
      new AT.FkStaffAttribute({
        name: 'assignee',
        joinPath: ['staff!fk_action_history_assignee_id_staff'],
        column: 'assignee_id',
        tableProperties: {
          header: 'hit-base.common.assignee',
        },
        filters: {user_id: 'not.is.null'},
      }),
      new AT.TagAttribute({
        name: 'tag',
        tableProperties: {
          header: 'hit-base.common.tags',
        },
      }),
      new AT.BooleanAttribute({
        name: 'hasNotifications',
        column: 'notification(id)',
        transform: (d) => d && d.length > 0,
      }),
    ];
  }

  static get messageAttributes() {
    return [
      new AT.DateAttribute({
        name: 'date',
        column: 'operation_date',
        tableProperties: {
          header: 'hit-app.common.date',
        },
      }),
      new AT.StringAttribute({
        name: 'message',
        column: 'message',
        tableProperties: {
          header: 'hit-app.action.message',
        },
      }),
      new AT.FkStaffAttribute({
        name: 'operator',
        joinPath: ['staff!fk_action_history_creation_user_id_staff'],
        column: 'creation_user_id',
        tableProperties: {
          header: 'hit-app.action.operator',
        },
      }),
      new AT.EnumerationAttribute({
        name: 'operation',
        enumType: 'ACTION_OPERATION',
        enumClass: HitOnlineEnums,
        tableProperties: {
          header: 'hit-app.common.operation',
        },
      }),
      new AT.BooleanAttribute({
        name: 'hasNotifications',
        column: 'notification(id)',
        transform: (d) => d && d.length > 0,
      }),
    ];
  }
  static get timeTrackingAttributes() {
    return [
      new AT.DateAttribute({
        name: 'date',
        column: 'operation_date',
        tableProperties: {
          header: 'hit-app.common.date',
        },
      }),
      new AT.FkStaffAttribute({
        name: 'operator',
        joinPath: ['staff!fk_action_history_creation_user_id_staff'],
        column: 'creation_user_id',
        tableProperties: {
          header: 'hit-app.action.operator',
        },
      }),
      new AT.StringAttribute({
        name: 'duration',
        column: 'duration',
        transform: (d) =>
          HitActionUtils.stringifyDuration(
            d,
            useConfigurationStore().userLanguageSnakeCase,
          ),
        tableProperties: {
          header: 'hit-app.common.duration',
        },
      }),
      new AT.EnumerationAttribute({
        name: 'operation',
        enumType: 'ACTION_OPERATION',
        enumClass: HitOnlineEnums,
        tableProperties: {
          header: 'hit-app.common.operation',
        },
      }),
      new AT.StringAttribute({
        name: 'message',
        column: 'message',
        tableProperties: {
          header: 'hit-app.action.message',
        },
      }),
    ];
  }

  async delete(router) {
    useActionStore()
      .deleteAction(this.id)
      .then((success) => {
        if (success) {
          this.redirectToList(router);
        }
      });
  }
}
